import { useState, useRef, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    FaShieldAlt,
    FaSync,
    FaUserSlash,
    FaUserCheck,
    FaKey,
    FaSignOutAlt,
    FaChevronDown,
    FaChevronRight,
    FaPlug,
    FaLaptop,
    FaCheckCircle,
    FaExclamationTriangle,
    FaLink,
    FaUnlink,
    FaPowerOff,
    FaIdBadge,
    FaBuilding,
    FaEnvelope,
    FaPhone,
    FaMapMarkerAlt,
    FaTerminal,
    FaDesktop,
    FaMobileAlt,
    FaInfoCircle,
    FaHeadset,
    FaTicketAlt,
    FaExternalLinkAlt
} from 'react-icons/fa';
import { FiEdit2, FiCheck, FiX } from 'react-icons/fi';

const UserRow = ({
    user: u,
    isExpanded,
    onToggle,
    loadingDetail,
    devices,
    security,
    actionLoading,
    formatDate,
    getComplianceBadge,
    onSyncDevice,
    onRebootDevice,
    onConnectDevice,
    onDisableUser,
    onEnableUser,
    onRevokeSessionsUser,
    onResetPassword,
    onUpdateUser,
    onSetManager,
    onOpenScriptRunner,
    onOpenDeviceDetail,
    apiFetch
}) => {
    const [editing, setEditing] = useState(false);
    const [editFields, setEditFields] = useState({});
    const [deviceTooltip, setDeviceTooltip] = useState(null);
    const deviceTooltipTimer = useRef(null);

    // IT Service Desk link-back state
    const [openTickets, setOpenTickets] = useState([]);
    const [ticketsLoading, setTicketsLoading] = useState(false);
    const [ticketsLoaded, setTicketsLoaded] = useState(false);

    const navigate = useNavigate();

    // Load open IT tickets when a user's row is expanded.
    // Tickets are linked by the requester's CapTrac UserID where available, with a
    // fallback to the device-owner's email address so that users WITHOUT a CapTrac
    // account on the system still have their IT tickets surfaced here.
    const ownerEmail = u.mail || u.userPrincipalName || '';
    const loadOpenTickets = useCallback(async () => {
        if (ticketsLoaded || !apiFetch) return;
        // Need at least one way to identify the user: a linked account or an email.
        if (!u.capTracUserID && !ownerEmail) return;
        setTicketsLoading(true);
        try {
            // Route requires an int id; pass 0 for unlinked users and rely on the
            // email query-param fallback handled server-side.
            const userIdSegment = u.capTracUserID || 0;
            const query = ownerEmail ? `?email=${encodeURIComponent(ownerEmail)}` : '';
            const res = await apiFetch(`/api/UnifiedEndpoint/users/${userIdSegment}/tickets${query}`);
            if (res.ok) {
                const data = await res.json();
                setOpenTickets(data.tickets || []);
            }
        } catch {
            // Non-critical: tickets panel degrades gracefully on failure
        } finally {
            setTicketsLoading(false);
            setTicketsLoaded(true);
        }
    }, [u.capTracUserID, ownerEmail, ticketsLoaded, apiFetch]);

    // Trigger ticket load when the row is expanded (linked OR email-resolvable user)
    useEffect(() => {
        if (isExpanded && (u.capTracUserID || ownerEmail) && !ticketsLoaded) {
            loadOpenTickets();
        }
        // Reset tickets when row collapses so fresh data is fetched on next expand
        if (!isExpanded) {
            setTicketsLoaded(false);
            setOpenTickets([]);
        }
    }, [isExpanded, u.capTracUserID, ownerEmail, ticketsLoaded, loadOpenTickets]);

    /**
     * Navigate to Action Items page and auto-open the create modal with an IT ticket type pre-selected.
     */
    const handleCreateTicket = (e) => {
        e.stopPropagation();
        navigate('/admin/action-items', {
            state: {
                openCreate: true,
                selectedType: 'IT Support Request'
            }
        });
    };

    /** Map ticket status to a badge class */
    const getTicketStatusClass = (status) => {
        if (!status) return 'uem-badge--unknown';
        const s = status.toLowerCase();
        if (s === 'open') return 'uem-badge--warning';
        if (s === 'in progress' || s === 'inprogress') return 'uem-badge--info';
        if (s === 'resolved') return 'uem-badge--success';
        return 'uem-badge--unknown';
    };

    const startEdit = (e) => {
        e.stopPropagation();
        setEditFields({
            mobilePhone: u.mobilePhone || '',
            department: u.department || '',
            jobTitle: u.jobTitle || '',
            officeLocation: u.officeLocation || ''
        });
        setEditing(true);
    };

    const cancelEdit = (e) => {
        e.stopPropagation();
        setEditing(false);
    };

    const saveEdit = async (e) => {
        e.stopPropagation();
        await onUpdateUser(u.entraUserId, editFields);
        setEditing(false);
    };

    return (
        <>
            <tr className={`uem-row ${isExpanded ? 'uem-row--expanded' : ''}`} onClick={onToggle}>
                <td className="uem-expand-cell">
                    {isExpanded ? <FaChevronDown /> : <FaChevronRight />}
                </td>
                <td className="uem-name-cell">
                    <span className="uem-user-name">{u.displayName}</span>
                </td>
                <td>{u.mail || u.userPrincipalName}</td>
                <td>{u.jobTitle || '—'}</td>
                <td>{u.department || '—'}</td>
                <td>
                    {u.accountEnabled
                        ? <span className="uem-badge uem-badge--success">Active</span>
                        : <span className="uem-badge uem-badge--danger">Disabled</span>
                    }
                </td>
                <td className="uem-date-cell">{formatDate(u.lastSignInDateTime)}</td>
                <td>
                    {u.capTracUserID
                        ? <span className="uem-badge uem-badge--info" title={`CapTrac: ${u.capTracUserName}`}><FaLink /> {u.capTracUserName}</span>
                        : <span className="uem-badge uem-badge--muted"><FaUnlink /> Not Linked</span>
                    }
                </td>
                <td className="uem-actions-cell" onClick={(e) => e.stopPropagation()}>
                    <div className="uem-action-buttons">
                        {u.accountEnabled ? (
                            <button
                                type="button"
                                className="uem-action-btn uem-action-btn--danger"
                                title="Disable Account"
                                aria-label="Disable Account"
                                disabled={actionLoading[`disable-${u.entraUserId}`]}
                                onClick={() => onDisableUser(u.entraUserId, u.displayName)}
                            >
                                <FaUserSlash />
                            </button>
                        ) : (
                            <button
                                type="button"
                                className="uem-action-btn uem-action-btn--success"
                                title="Enable Account"
                                aria-label="Enable Account"
                                disabled={actionLoading[`enable-${u.entraUserId}`]}
                                onClick={() => onEnableUser(u.entraUserId)}
                            >
                                <FaUserCheck />
                            </button>
                        )}
                        <button
                            type="button"
                            className="uem-action-btn uem-action-btn--warning"
                            title="Revoke Sessions"
                            aria-label="Revoke Sessions"
                            disabled={actionLoading[`revoke-${u.entraUserId}`]}
                            onClick={() => onRevokeSessionsUser(u.entraUserId, u.displayName)}
                        >
                            <FaSignOutAlt />
                        </button>
                        <button
                            type="button"
                            className="uem-action-btn uem-action-btn--primary"
                            title="Reset Password"
                            aria-label="Reset Password"
                            disabled={actionLoading[`reset-${u.entraUserId}`]}
                            onClick={() => onResetPassword(u.entraUserId, u.displayName)}
                        >
                            <FaKey />
                        </button>
                    </div>
                </td>
            </tr>

            {/* Expanded detail panel */}
            {isExpanded && (
                <tr className="uem-detail-row">
                    <td colSpan="9">
                        {loadingDetail ? (
                            <div className="uem-detail-loading"><FaSync className="uem-spin" /> Loading details...</div>
                        ) : (
                            <div className="uem-detail-panel">
                                {/* User Info Card */}
                                <div className="uem-detail-section">
                                    <h3 className="uem-detail-heading">
                                        <FaIdBadge /> User Details
                                        {!editing ? (
                                            <button type="button" className="uem-edit-btn" onClick={startEdit} title="Edit user details" aria-label="Edit user details"><FiEdit2 /></button>
                                        ) : (
                                            <span className="uem-edit-actions">
                                                <button type="button" className="uem-edit-save" onClick={saveEdit} title="Save" aria-label="Save edits"><FiCheck /></button>
                                                <button type="button" className="uem-edit-cancel" onClick={cancelEdit} title="Cancel" aria-label="Cancel edits"><FiX /></button>
                                            </span>
                                        )}
                                    </h3>
                                    <div className="uem-detail-grid">
                                        <div className="uem-detail-item">
                                            <FaEnvelope className="uem-detail-icon" />
                                            <span className="uem-detail-label">Email</span>
                                            <span className="uem-detail-value">{u.mail || u.userPrincipalName}</span>
                                        </div>
                                        <div className="uem-detail-item">
                                            <FaPhone className="uem-detail-icon" />
                                            <span className="uem-detail-label">Mobile</span>
                                            {editing ? (
                                                <input className="uem-edit-input" value={editFields.mobilePhone} onClick={e => e.stopPropagation()} onChange={e => setEditFields(f => ({ ...f, mobilePhone: e.target.value }))} />
                                            ) : (
                                                <span className="uem-detail-value">{u.mobilePhone || '—'}</span>
                                            )}
                                        </div>
                                        <div className="uem-detail-item">
                                            <FaMapMarkerAlt className="uem-detail-icon" />
                                            <span className="uem-detail-label">Office</span>
                                            {editing ? (
                                                <input className="uem-edit-input" value={editFields.officeLocation} onClick={e => e.stopPropagation()} onChange={e => setEditFields(f => ({ ...f, officeLocation: e.target.value }))} />
                                            ) : (
                                                <span className="uem-detail-value">{u.officeLocation || '—'}</span>
                                            )}
                                        </div>
                                        <div className="uem-detail-item">
                                            <FaBuilding className="uem-detail-icon" />
                                            <span className="uem-detail-label">Department</span>
                                            {editing ? (
                                                <input className="uem-edit-input" value={editFields.department} onClick={e => e.stopPropagation()} onChange={e => setEditFields(f => ({ ...f, department: e.target.value }))} />
                                            ) : (
                                                <span className="uem-detail-value">{u.department || '—'}</span>
                                            )}
                                        </div>
                                        <div className="uem-detail-item">
                                            <FaIdBadge className="uem-detail-icon" />
                                            <span className="uem-detail-label">Job Title</span>
                                            {editing ? (
                                                <input className="uem-edit-input" value={editFields.jobTitle} onClick={e => e.stopPropagation()} onChange={e => setEditFields(f => ({ ...f, jobTitle: e.target.value }))} />
                                            ) : (
                                                <span className="uem-detail-value">{u.jobTitle || '—'}</span>
                                            )}
                                        </div>
                                    </div>
                                </div>

                                {/* Security Summary */}
                                {security && (
                                    <div className="uem-detail-section">
                                        <h3 className="uem-detail-heading"><FaShieldAlt /> Security &amp; Compliance</h3>
                                        <div className="uem-security-grid">
                                            {['total', 'compliant', 'noncompliant'].map(type => {
                                                const count = type === 'total' ? security.totalDevices : type === 'compliant' ? security.compliantDevices : security.nonCompliantDevices;
                                                const label = type === 'total' ? 'Total Devices' : type === 'compliant' ? 'Compliant' : 'Non-Compliant';
                                                const cardClass = type === 'compliant' ? 'uem-security-card--success' : type === 'noncompliant' ? 'uem-security-card--danger' : '';
                                                const allDevices = security.deviceList || [];
                                                const filteredDevices = type === 'total' ? allDevices
                                                    : type === 'compliant' ? allDevices.filter(d => d.complianceState?.toLowerCase() === 'compliant')
                                                    : allDevices.filter(d => d.complianceState?.toLowerCase() !== 'compliant');

                                                return (
                                                    <div
                                                        key={type}
                                                        className={`uem-security-card ${cardClass}`}
                                                        style={{ position: 'relative', cursor: count > 0 ? 'pointer' : 'default' }}
                                                        onMouseEnter={() => {
                                                            if (count > 0) {
                                                                deviceTooltipTimer.current = setTimeout(() => setDeviceTooltip(type), 400);
                                                            }
                                                        }}
                                                        onMouseLeave={() => {
                                                            clearTimeout(deviceTooltipTimer.current);
                                                            setDeviceTooltip(null);
                                                        }}
                                                    >
                                                        <div className="uem-security-number">{count}</div>
                                                        <div className="uem-security-label">{label}</div>
                                                        {deviceTooltip === type && filteredDevices.length > 0 && (
                                                            <div className="uem-device-tooltip" onClick={e => e.stopPropagation()}>
                                                                {filteredDevices.map(d => (
                                                                    <div key={d.deviceId} className="uem-device-tooltip-row">
                                                                        <span className="uem-device-tooltip-name">
                                                                            {d.operatingSystem?.toLowerCase().includes('windows') ? <FaDesktop style={{ marginRight: 4 }} /> : <FaLaptop style={{ marginRight: 4 }} />}
                                                                            {d.deviceName}
                                                                        </span>
                                                                        <button
                                                                            type="button"
                                                                            className="uem-device-tooltip-btn"
                                                                            title="Open device details"
                                                                            aria-label={`Open details for ${d.deviceName}`}
                                                                            onClick={(e) => { e.stopPropagation(); setDeviceTooltip(null); onOpenDeviceDetail(d); }}
                                                                        >
                                                                            <FaInfoCircle /> Details
                                                                        </button>
                                                                    </div>
                                                                ))}
                                                            </div>
                                                        )}
                                                    </div>
                                                );
                                            })}
                                        </div>

                                        {/* Manager */}
                                        <div className="uem-manager-section">
                                            <span className="uem-tag-label">Manager:</span>
                                            <span className="uem-manager-name">{security.managerDisplayName || 'Not set'}</span>
                                            {security.managerMail && <span className="uem-manager-email">({security.managerMail})</span>}
                                            <button type="button" className="uem-edit-btn" title="Change manager" aria-label="Change manager" onClick={(e) => { e.stopPropagation(); onSetManager(u.entraUserId, u.displayName); }}>
                                                <FiEdit2 />
                                            </button>
                                        </div>

                                        {security.assignedLicenses?.length > 0 && (
                                            <div className="uem-tag-section">
                                                <span className="uem-tag-label">Licenses:</span>
                                                <div className="uem-tags">
                                                    {security.assignedLicenses.map((l, i) => (
                                                        <span key={i} className="uem-tag">{l}</span>
                                                    ))}
                                                </div>
                                            </div>
                                        )}

                                        {security.groupMemberships?.length > 0 && (
                                            <div className="uem-tag-section">
                                                <span className="uem-tag-label">Groups:</span>
                                                <div className="uem-tags">
                                                    {security.groupMemberships.slice(0, 10).map((g, i) => (
                                                        <span key={i} className="uem-tag uem-tag--group">{g}</span>
                                                    ))}
                                                    {security.groupMemberships.length > 10 && (
                                                        <span className="uem-tag uem-tag--more">+{security.groupMemberships.length - 10} more</span>
                                                    )}
                                                </div>
                                            </div>
                                        )}

                                        {/* Risk Status */}
                                        {security.riskLevel && security.riskLevel !== 'None' && (
                                            <div className="uem-risk-banner" data-risk={security.riskLevel?.toLowerCase()}>
                                                <FaExclamationTriangle />
                                                <span>Risk Level: <strong>{security.riskLevel}</strong></span>
                                                <span className="uem-risk-state">State: {security.riskState || 'Unknown'}</span>
                                            </div>
                                        )}

                                        {/* Security Alerts */}
                                        {security.recentAlerts?.length > 0 && (
                                            <div className="uem-alerts-section">
                                                <span className="uem-tag-label">Recent Alerts:</span>
                                                <div className="uem-alerts-list">
                                                    {security.recentAlerts.map((a, i) => (
                                                        <div key={i} className="uem-alert-item" data-severity={a.severity?.toLowerCase()}>
                                                            <span className="uem-alert-severity">{a.severity}</span>
                                                            {a.alertWebUrl ? (
                                                                <a href={a.alertWebUrl} target="_blank" rel="noopener noreferrer" className="uem-alert-title uem-alert-link">{a.title}</a>
                                                            ) : (
                                                                <span className="uem-alert-title">{a.title}</span>
                                                            )}
                                                            <span className="uem-alert-status">{a.status}</span>
                                                        </div>
                                                    ))}
                                                </div>
                                            </div>
                                        )}

                                        {/* Authentication Methods */}
                                        {security.authenticationMethods?.length > 0 && (
                                            <div className="uem-tag-section">
                                                <span className="uem-tag-label">Auth Methods:</span>
                                                <div className="uem-tags">
                                                    {security.authenticationMethods.map((m, i) => (
                                                        <span key={i} className="uem-tag uem-tag--auth">{m.replace('AuthenticationMethod', '').replace(/([A-Z])/g, ' $1').trim()}</span>
                                                    ))}
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                )}

                                {/* Devices */}
                                <div className="uem-detail-section">
                                    <h3 className="uem-detail-heading"><FaLaptop /> Recent Devices</h3>
                                    {devices.length === 0 ? (
                                        <p className="uem-no-data">No managed devices found for this user.</p>
                                    ) : (
                                        <>
                                            {devices[0]?.associationType === 'ActiveSession' && (
                                                <p className="uem-association-note"><FaCheckCircle style={{ color: '#27ae60', marginRight: 4 }} /> Showing devices with active sessions (last 24h)</p>
                                            )}
                                            {devices[0]?.associationType === 'PrimaryUser' && (
                                                <p className="uem-association-note" style={{ color: '#e67e22' }}><FaExclamationTriangle style={{ marginRight: 4 }} /> No active sessions found — showing primary user devices</p>
                                            )}
                                            <div className="uem-devices-grid">
                                                {devices.map(d => (
                                                    <div key={d.deviceId} className="uem-device-card">
                                                        <div className="uem-device-header">
                                                            <div className="uem-device-icon">
                                                                {d.operatingSystem?.toLowerCase().includes('windows') ? <FaDesktop /> :
                                                                 d.operatingSystem?.toLowerCase().includes('android') || d.operatingSystem?.toLowerCase().includes('ios') ? <FaMobileAlt /> :
                                                                 <FaLaptop />}
                                                            </div>
                                                            <div className="uem-device-title">
                                                                <h4>{d.deviceName}</h4>
                                                                <span className="uem-device-os">{d.operatingSystem} {d.osVersion}</span>
                                                            </div>
                                                            {getComplianceBadge(d.complianceState)}
                                                        </div>

                                                        <div className="uem-device-details">
                                                            <div className="uem-device-detail">
                                                                <span className="uem-device-detail-label">Model</span>
                                                                <span>{d.manufacturer} {d.model}</span>
                                                            </div>
                                                            <div className="uem-device-detail">
                                                                <span className="uem-device-detail-label">Last Sync</span>
                                                                <span>{formatDate(d.lastSyncDateTime)}</span>
                                                            </div>
                                                            <div className="uem-device-detail">
                                                                <span className="uem-device-detail-label">Enrolled</span>
                                                                <span>{formatDate(d.enrolledDateTime)}</span>
                                                            </div>
                                                            {d.lastLoggedOnDateTime && (
                                                                <div className="uem-device-detail">
                                                                    <span className="uem-device-detail-label">Last Login</span>
                                                                    <span>{formatDate(d.lastLoggedOnDateTime)}</span>
                                                                </div>
                                                            )}
                                                            {d.serialNumber && (
                                                                <div className="uem-device-detail">
                                                                    <span className="uem-device-detail-label">Serial</span>
                                                                    <span>{d.serialNumber}</span>
                                                                </div>
                                                            )}
                                                            <div className="uem-device-detail">
                                                                <span className="uem-device-detail-label">Encrypted</span>
                                                                <span>{d.isEncrypted ? '✓ Yes' : '✗ No'}</span>
                                                            </div>
                                                        </div>

                                                        <div className="uem-device-actions">
                                                            <button
                                                                type="button"
                                                                className="uem-device-action-btn uem-device-action-btn--connect"
                                                                title="Connect via Splashtop"
                                                                aria-label={`Connect to ${d.deviceName} via Splashtop`}
                                                                disabled={actionLoading[`connect-${d.deviceName}`]}
                                                                onClick={() => onConnectDevice(d.deviceName, d.splashtopStreamerId, d.wiFiMacAddress, d.ethernetMacAddress)}
                                                            >
                                                                <FaPlug /> Connect
                                                            </button>
                                                            <button
                                                                type="button"
                                                                className="uem-device-action-btn uem-device-action-btn--script"
                                                                title="Run Script"
                                                                aria-label={`Run script on ${d.deviceName}`}
                                                                onClick={() => onOpenScriptRunner(d)}
                                                            >
                                                                <FaTerminal /> Scripts
                                                            </button>
                                                            <button
                                                                type="button"
                                                                className="uem-device-action-btn uem-device-action-btn--info"
                                                                title="Full Device Details"
                                                                aria-label={`View details for ${d.deviceName}`}
                                                                onClick={() => onOpenDeviceDetail(d)}
                                                            >
                                                                <FaInfoCircle /> Details
                                                            </button>
                                                            <button
                                                                type="button"
                                                                className="uem-device-action-btn"
                                                                title="Sync Device"
                                                                aria-label={`Sync ${d.deviceName}`}
                                                                disabled={actionLoading[`sync-${d.deviceId}`]}
                                                                onClick={() => onSyncDevice(d.deviceId)}
                                                            >
                                                                <FaSync /> Sync
                                                            </button>
                                                            <button
                                                                type="button"
                                                                className="uem-device-action-btn uem-device-action-btn--danger"
                                                                title="Reboot Device"
                                                                aria-label={`Reboot ${d.deviceName}`}
                                                                disabled={actionLoading[`reboot-${d.deviceId}`]}
                                                                onClick={() => onRebootDevice(d.deviceId)}
                                                            >
                                                                <FaPowerOff /> Reboot
                                                            </button>
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                        </>
                                    )}
                                </div>

                                {/* IT Service Management Link-Back */}
                                <div className="uem-detail-section">
                                    <h3 className="uem-detail-heading">
                                        <FaHeadset /> IT Support Tickets
                                        <button
                                            type="button"
                                            className="uem-action-btn uem-action-btn--success"
                                            style={{ marginLeft: 'auto', fontSize: '0.8rem', padding: '4px 10px' }}
                                            title="Create a support ticket for this user"
                                            aria-label={`Create support ticket for ${u.displayName}`}
                                            onClick={handleCreateTicket}
                                        >
                                            <FaTicketAlt /> New Ticket
                                        </button>
                                    </h3>

                                    {!u.capTracUserID && !ownerEmail ? (
                                        <p className="uem-no-data">
                                            No CapTrac account or email is available for this user, so service history cannot be matched.
                                        </p>
                                    ) : ticketsLoading ? (
                                        <div className="uem-detail-loading"><FaSync className="uem-spin" /> Loading tickets...</div>
                                    ) : openTickets.length === 0 ? (
                                        <p className="uem-no-data">
                                            No open support tickets found for this user
                                            {!u.capTracUserID && ownerEmail ? ' (matched by email — no linked CapTrac account).' : '.'}
                                        </p>
                                    ) : (
                                        <div className="uem-tickets-list">
                                            {openTickets.map(ticket => (
                                                <div key={ticket.id} className="uem-ticket-row">
                                                    <span className={`uem-badge ${getTicketStatusClass(ticket.status)}`}>
                                                        {ticket.status}
                                                    </span>
                                                    <span className="uem-ticket-ref">{ticket.ticketReference || `#${ticket.id}`}</span>
                                                    <span className="uem-ticket-title">{ticket.title}</span>
                                                    <span className="uem-ticket-priority" data-priority={ticket.priority?.toLowerCase()}>
                                                        {ticket.priority}
                                                    </span>
                                                    <span className="uem-ticket-date">{formatDate(ticket.createdDate)}</span>
                                                    <a
                                                        href={`/action-items/${ticket.id}`}
                                                        className="uem-ticket-link"
                                                        title="View ticket details"
                                                        aria-label={`View ticket ${ticket.ticketReference || ticket.id}`}
                                                        onClick={e => { e.stopPropagation(); navigate(`/action-items/${ticket.id}`); }}
                                                    >
                                                        <FaExternalLinkAlt />
                                                    </a>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            </div>
                        )}
                    </td>
                </tr>
            )}
        </>
    );
};

export default UserRow;
